import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';

const requestSchema = z.object({
  candidate_name: z.string().min(2, "Le nom est requis"),
  candidate_email: z.string().email("Email invalide"),
  job_title: z.string().min(2, "L'intitulé du poste est requis"),
  context_type: z.enum(["recrutement", "mission", "managérial", "collaboration"]),
  direction_type: z.enum([
    "candidate_to_recruiter", 
    "recruiter_to_candidate", 
    "freelance_to_client", 
    "client_to_freelance",
    "employee_to_manager",
    "manager_to_employee",
    "peer_to_peer"
  ]),
  send_email: z.boolean().default(true),
  message: z.string().optional(),
  projectId: z.string().optional(),
});

type RequestFormValues = z.infer<typeof requestSchema>;

const FeedbackRequestForm = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState<{id: string; name: string; category: string}[]>([]);
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
    name: '', 
    description: '', 
    category: 'recrutement' 
  });

  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      candidate_name: '',
      candidate_email: '',
      job_title: '',
      context_type: 'recrutement',
      direction_type: profile?.role === 'candidat' ? 'candidate_to_recruiter' : 'recruiter_to_candidate',
      send_email: true,
      message: '',
      projectId: '',
    },
  });

  const contextType = form.watch('context_type');
  
  useEffect(() => {
    if (contextType === 'recrutement') {
      form.setValue('direction_type', profile?.role === 'candidat' ? 'candidate_to_recruiter' : 'recruiter_to_candidate');
    } else if (contextType === 'mission') {
      form.setValue('direction_type', profile?.role === 'candidat' ? 'freelance_to_client' : 'client_to_freelance');
    } else if (contextType === 'managérial') {
      form.setValue('direction_type', 'employee_to_manager');
    } else if (contextType === 'collaboration') {
      form.setValue('direction_type', 'peer_to_peer');
    }
  }, [contextType, profile?.role, form]);

  useEffect(() => {
    if (user) {
      const fetchProjects = async () => {
        try {
          const { data, error } = await supabase
            .from('projects')
            .select('id, name, category')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (error) throw error;
          if (data) setProjects(data);
        } catch (error) {
          console.error('Erreur lors du chargement des projets:', error);
        }
      };
      
      fetchProjects();
    }
  }, [user]);

  const handleCreateProject = async () => {
    if (!user || !newProject.name.trim()) return;
    
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: newProject.name,
          description: newProject.description,
          category: newProject.category,
          user_id: user.id
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setProjects([...projects, data[0]]);
        form.setValue('projectId', data[0].id);
      }
      
      setCreateProjectDialogOpen(false);
      setNewProject({ name: '', description: '', category: form.getValues('context_type') || 'recrutement' });
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
    }
  };

  const onSubmit = async (data: RequestFormValues) => {
    if (!user) {
      toast.error("Vous devez être connecté pour effectuer cette action");
      return;
    }

    setIsSubmitting(true);

    try {
      let candidateName, candidateEmail, recruiterName, recruiterEmail, recruiterCompany;
      const directionType = data.direction_type;
      
      if (directionType === 'candidate_to_recruiter' || directionType === 'freelance_to_client') {
        candidateName = profile?.full_name || '';
        candidateEmail = profile?.email || '';
        recruiterName = data.candidate_name;
        recruiterEmail = data.candidate_email;
      } else {
        candidateName = data.candidate_name;
        candidateEmail = data.candidate_email;
        recruiterName = profile?.full_name || '';
        recruiterEmail = profile?.email || '';
        recruiterCompany = profile?.organization || '';
      }

      const { data: requestData, error } = await supabase
        .from('feedback_requests')
        .insert({
          user_id: user.id,
          candidate_name: candidateName,
          candidate_email: candidateEmail,
          recruiter_name: recruiterName,
          recruiter_email: recruiterEmail,
          recruiter_company: recruiterCompany,
          job_title: data.job_title,
          message: data.message,
          status: 'pending',
        })
        .select();

      if (error) throw error;

      console.log("Demande créée:", requestData);
      
      if (data.projectId && requestData && requestData.length > 0) {
        const { error: projectError } = await supabase
          .from('projects_requests')
          .insert({
            project_id: data.projectId,
            request_id: requestData[0].id,
            user_id: user.id
          });
        
        if (projectError) {
          console.error('Erreur lors de l\'association de la demande au projet:', projectError);
          toast.warning("Demande créée mais l'association au projet a échoué");
        }
      }

      if (data.send_email) {
        const { error: emailError } = await supabase.functions.invoke('send-feedback-request', {
          body: { 
            requestId: requestData?.[0]?.id,
            context_type: data.context_type,
            direction_type: data.direction_type
          }
        });
        
        if (emailError) {
          console.error("Erreur lors de l'envoi de l'email:", emailError);
          toast.warning("Demande créée mais l'email n'a pas pu être envoyé");
        }
      }

      toast.success("Demande envoyée avec succès", {
        description: "Le destinataire recevra un email avec votre demande de feedback",
      });

      navigate('/dashboard');
    } catch (error: any) {
      console.error("Erreur lors de la création de la demande:", error);
      toast.error("Une erreur est survenue", {
        description: error.message || "Impossible de créer la demande",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDirectionOptions = () => {
    switch (contextType) {
      case 'recrutement':
        return [
          { value: 'candidate_to_recruiter', label: 'Candidat -> Recruteur' },
          { value: 'recruiter_to_candidate', label: 'Recruteur -> Candidat' }
        ];
      case 'mission':
        return [
          { value: 'freelance_to_client', label: 'Freelance -> Client' },
          { value: 'client_to_freelance', label: 'Client -> Freelance' }
        ];
      case 'managérial':
        return [
          { value: 'employee_to_manager', label: 'Collaborateur -> Manager' },
          { value: 'manager_to_employee', label: 'Manager -> Collaborateur' }
        ];
      case 'collaboration':
        return [
          { value: 'peer_to_peer', label: 'Collègue -> Collègue' }
        ];
      default:
        return [];
    }
  };

  const ProjectSelector = () => (
    <FormField
      control={form.control}
      name="projectId"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Associer à un projet (optionnel)</FormLabel>
          <div className="flex gap-2">
            <Select
              value={field.value}
              onValueChange={field.onChange}
            >
              <FormControl>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Sélectionner un projet ou aucun" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="none">Aucun projet</SelectItem>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              type="button" 
              variant="outline" 
              className="flex gap-1 items-center"
              onClick={() => {
                setNewProject({...newProject, category: form.getValues('context_type') || 'recrutement'});
                setCreateProjectDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouveau
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Demander un feedback</CardTitle>
        <CardDescription>
          Envoyez une demande personnalisée pour recevoir un retour constructif
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="candidate_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input placeholder="Nom du destinataire" {...field} />
                  </FormControl>
                  <FormDescription>
                    {form.getValues('direction_type') === 'candidate_to_recruiter' || form.getValues('direction_type') === 'freelance_to_client' 
                      ? "Nom du recruteur/client à qui vous demandez un feedback" 
                      : "Nom du candidat/freelance pour qui vous demandez un feedback"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="candidate_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Email du destinataire" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="job_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Poste / Mission</FormLabel>
                  <FormControl>
                    <Input placeholder="Intitulé du poste ou nom du projet" {...field} />
                  </FormControl>
                  <FormDescription>
                    {contextType === 'recrutement' ? "Intitulé du poste concerné" : 
                     contextType === 'mission' ? "Nom du projet ou de la mission" :
                     "Position ou contexte de collaboration"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="context_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contexte</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un contexte" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="recrutement">Recrutement</SelectItem>
                        <SelectItem value="mission">Mission / Projet</SelectItem>
                        <SelectItem value="managérial">Managérial</SelectItem>
                        <SelectItem value="collaboration">Collaboration</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="direction_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Direction</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une direction" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getDirectionOptions().map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <ProjectSelector />
            
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (optionnel)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Ajoutez un message personnel à votre demande de feedback" 
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="send_email"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Envoyer un email</FormLabel>
                    <FormDescription>
                      Le destinataire recevra un email l'invitant à vous fournir un feedback
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
        <Button 
          onClick={form.handleSubmit(onSubmit)} 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
        </Button>
      </CardFooter>

      <Dialog open={createProjectDialogOpen} onOpenChange={setCreateProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un nouveau projet</DialogTitle>
            <DialogDescription>
              Organisez vos feedbacks et demandes en projets
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Nom du projet</Label>
              <Input 
                id="project-name" 
                value={newProject.name}
                onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                placeholder="Ex: Recrutement Développeurs Frontend"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea 
                id="project-description"
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="Description du projet (optionnelle)"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-category">Catégorie</Label>
              <Select
                value={newProject.category}
                onValueChange={(value) => setNewProject({...newProject, category: value})}
              >
                <SelectTrigger id="project-category">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recrutement">Recrutement</SelectItem>
                  <SelectItem value="candidature">Candidature</SelectItem>
                  <SelectItem value="mission">Mission</SelectItem>
                  <SelectItem value="managérial">Managérial</SelectItem>
                  <SelectItem value="collaboration">Collaboration</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateProjectDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateProject}>Créer le projet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FeedbackRequestForm;
